/**
 * The input forms the tree constructors accept, and their conversion to the flat,
 * interleaved layout the wasm module takes.
 *
 * Every geometry type has a flat form (typed arrays in the GeoArrow layout, the fastest
 * path: each array crosses into wasm with a single copy), a nested-array form, and a
 * GeoJSON form. The GeoJSON types here are structural, so values typed with
 * `@types/geojson` are accepted without conversion.
 */
/**
 * Converts any accepted {@link Coordinates} form to the interleaved `Float64Array` the
 * wasm module takes. A `Float64Array` is returned as is.
 */
export function flattenCoordinates(coords) {
    if (coords instanceof Float64Array) {
        return coords;
    }
    if (coords.length === 0) {
        return new Float64Array(0);
    }
    if (typeof coords[0] === "number") {
        return Float64Array.from(coords);
    }
    const positions = coords;
    const flat = new Float64Array(positions.length * 2);
    for (let i = 0; i < positions.length; i++) {
        writePosition(flat, i, positions[i]);
    }
    return flat;
}
/** Converts any accepted {@link LineStrings} form to the flat CSR layout, as typed arrays. */
export function flattenLineStrings(lines) {
    if (!Array.isArray(lines)) {
        const arrays = lines;
        return {
            coords: toFloat64(arrays.coords),
            offsets: toUint32(arrays.offsets),
        };
    }
    const parts = lines;
    const offsets = new Uint32Array(parts.length + 1);
    let total = 0;
    for (let i = 0; i < parts.length; i++) {
        offsets[i] = total;
        total += parts[i].length;
    }
    offsets[parts.length] = total;
    const coords = new Float64Array(total * 2);
    let vertex = 0;
    for (const line of parts) {
        for (const position of line) {
            writePosition(coords, vertex++, position);
        }
    }
    return { coords, offsets };
}
/** Converts any accepted {@link Polygons} form to the flat two-level CSR layout, as typed arrays. */
export function flattenPolygons(polygons) {
    if (!Array.isArray(polygons)) {
        const arrays = polygons;
        return {
            coords: toFloat64(arrays.coords),
            ringOffsets: toUint32(arrays.ringOffsets),
            polygonOffsets: toUint32(arrays.polygonOffsets),
        };
    }
    const parts = polygons;
    let ringCount = 0;
    let vertexCount = 0;
    for (const polygon of parts) {
        ringCount += polygon.length;
        for (const ring of polygon) {
            vertexCount += ring.length;
        }
    }
    const polygonOffsets = new Uint32Array(parts.length + 1);
    const ringOffsets = new Uint32Array(ringCount + 1);
    const coords = new Float64Array(vertexCount * 2);
    let ring = 0;
    let vertex = 0;
    for (let i = 0; i < parts.length; i++) {
        polygonOffsets[i] = ring;
        for (const positions of parts[i]) {
            ringOffsets[ring++] = vertex;
            for (const position of positions) {
                writePosition(coords, vertex++, position);
            }
        }
    }
    polygonOffsets[parts.length] = ring;
    ringOffsets[ringCount] = vertex;
    return { coords, ringOffsets, polygonOffsets };
}
/**
 * The geometries of a GeoJSON input, in order, checked to all be of `type`. Throws a
 * `TypeError` naming the offending position on a `null` geometry or a type mismatch.
 */
export function geoJSONGeometries(input, type) {
    const items = Array.isArray(input)
        ? input
        : input.features;
    const geometries = [];
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const geometry = item.type === "Feature" ? item.geometry : item;
        if (geometry === null) {
            throw new TypeError(`GeoJSON feature ${i} has a null geometry`);
        }
        if (geometry.type !== type) {
            throw new TypeError(`GeoJSON geometry ${i} is a ${geometry.type}; expected ${type}`);
        }
        geometries.push(geometry);
    }
    return geometries;
}
function writePosition(flat, index, position) {
    flat[index * 2] = position[0];
    flat[index * 2 + 1] = position[1];
}
function toFloat64(values) {
    return values instanceof Float64Array ? values : Float64Array.from(values);
}
function toUint32(values) {
    return values instanceof Uint32Array ? values : Uint32Array.from(values);
}
//# sourceMappingURL=geometry.js.map